// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import { useState } from 'react'
import { Search, X, Globe, AlertTriangle, FileText } from 'lucide-react'
import * as api from '../../api/client'
import { formatPhone } from '../../api/format'
import { useAuth } from '../../auth/AuthContext'

/**
 * Global Order Search (Sprint 18, Shipping Admin only).
 *
 * Cross-pharmacy lookup of any Roadpas order. Returns full PHI plus the
 * destination pharmacy name. Every call is audited server-side as
 * 'shipping.global_lookup' with the order number and the result pharmacy.
 *
 * Distinct from the per-pharmacy carrier-lookup on the Shipments page:
 *   - Here: any pharmacy's order, full PHI shown, NOT importable.
 *   - There: ownership-checked, PHI only on YOUR orders, importable.
 *
 * Surfaced in the nav only to users who hold 'shipping.global_lookup'.
 * Route-level RequirePerm provides defense-in-depth.
 */
export default function GlobalOrderSearchPage() {
  const { hasPermission } = useAuth()
  const [order, setOrder] = useState('')
  const [submitted, setSubmitted] = useState('')
  const [result, setResult] = useState<api.GlobalLookupResult | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  if (!hasPermission('shipping.global_lookup')) {
    // Defense in depth: nav already hides this entry, and the route should
    // guard it, but render a clean message if someone navigates here directly.
    return (
      <div style={page}>
        <h1 style={h1}>Not authorized</h1>
        <p style={muted}>You don't have permission to perform global order lookups.</p>
      </div>
    )
  }

  const doLookup = async () => {
    const o = order.trim()
    if (!o) return
    setLoading(true); setError(null); setResult(null); setSubmitted(o)
    try {
      const r = await api.globalOrderLookup(o)
      setResult(r)
    } catch (e: any) {
      setError(e?.message || 'Lookup failed.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div style={page}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
        <Globe size={22} style={{ color: '#a16207' }} />
        <h1 style={{ ...h1, margin: 0 }}>Global order search</h1>
      </div>
      <p style={{ ...muted, marginTop: 0 }}>
        Cross-pharmacy lookup. Search any Roadpas order number to see its full details
        regardless of pharmacy. <strong style={{ color: '#a16207' }}>Every lookup is audited.</strong>
      </p>

      {/* Privileged-action notice */}
      <div style={privNotice}>
        <AlertTriangle size={14} style={{ color: '#a16207', flexShrink: 0, marginTop: 2 }} />
        <div>
          <div style={{ fontWeight: 600, fontSize: 13 }}>Privileged action</div>
          <div style={{ fontSize: 12, color: 'var(--color-text-muted)', marginTop: 2 }}>
            This search displays personal health information (recipient name, address, phone) for shipments
            that may belong to other pharmacies. Use only for cross-pharmacy support, misrouted package
            resolution, or compliance investigations. Every search is logged with your account and the
            order number.
          </div>
        </div>
      </div>

      {/* Search box */}
      <div style={searchRow}>
        <div style={{ position: 'relative', flex: 1 }}>
          <Search size={14} style={{ position: 'absolute', left: 10, top: 11, color: 'var(--color-text-muted)' }} />
          <input
            type="search"
            value={order}
            onChange={e => setOrder(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') doLookup() }}
            placeholder="Roadpas order number (e.g. 2365840)"
            style={searchInput}
          />
          {order && (
            <button type="button" onClick={() => setOrder('')} style={clearBtn} title="Clear">
              <X size={13} />
            </button>
          )}
        </div>
        <button type="button" style={lookupBtn} disabled={loading || !order.trim()} onClick={doLookup}>
          {loading ? 'Searching…' : 'Search'}
        </button>
      </div>

      {error && <div style={errBox}>{error}</div>}

      {result && (
        <div style={{ marginTop: 18 }}>
          {result.found
            ? <GlobalResultCard r={result} />
            : (
              <div style={card}>
                <div style={{ fontWeight: 600 }}>Order {submitted} not found on the carrier</div>
                <div style={{ fontSize: 12, marginTop: 4, color: 'var(--color-text-muted)' }}>
                  {result.detail || 'No such order exists on Roadpas.'}
                </div>
              </div>
            )}
        </div>
      )}
    </div>
  )
}

// Pull the courier POD PDF for a cross-pharmacy order. The bytes stream from the carrier and are
// never stored on our side, so we open the blob URL in a new tab. Rendered for every result rather
// than only delivered ones — the server answers honestly ('courier_pod_unavailable') when the
// courier has no POD yet, which is more useful than a button that silently isn't there.
function PodDownload({ orderNumber }: { orderNumber?: string }) {
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  if (!orderNumber) return null
  const ord = orderNumber   // narrowed capture: TS drops param narrowing inside the closure

  async function pull() {
    setBusy(true); setErr(null)
    try {
      const doc = await api.globalOrderPod(ord)
      window.open(doc.url, '_blank', 'noopener')
    } catch (e) {
      const body = (e as { body?: { message?: string; error?: string } }).body
      setErr(body?.message || body?.error || (e as Error).message || 'Could not fetch the courier POD.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div style={{ marginTop: 10 }}>
      <button type="button" onClick={pull} disabled={busy}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', fontSize: 13,
          fontWeight: 600, cursor: busy ? 'default' : 'pointer', borderRadius: 6,
          border: '1px solid var(--color-border)', background: 'var(--color-surface, #fff)',
          color: 'var(--color-text)', opacity: busy ? 0.6 : 1 }}>
        <FileText size={14} /> {busy ? 'Fetching from courier…' : 'Courier POD (PDF)'}
      </button>
      {err && (
        <div style={{ marginTop: 6, fontSize: 12, color: 'var(--color-danger, #b91c1c)' }}>{err}</div>
      )}
    </div>
  )
}

function GlobalResultCard({ r }: { r: api.GlobalLookupResult }) {
  const dest = r.destination
  const pickup = r.pickup
  const hasLocal = !!r.local_match
  return (
    <div style={card}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
        <div style={{ fontSize: 18, fontWeight: 700 }}>Order {r.order_number}</div>
        <StatusPill status={r.status || 'created'} />
        <span style={typeTag}>Cross-pharmacy</span>
        <span style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>· Roadpas (e-Courier)</span>
        {r.service && <span style={chip}>{r.service}</span>}
      </div>

      {/* PRIMARY: Deliver to — patient name + address are what people came for */}
      {(dest || r.recipient_name) && (
        <section style={primaryBlock}>
          <div style={primaryLbl}>Deliver to</div>
          <div style={{ fontSize: 18, fontWeight: 700 }}>{dest?.name || r.recipient_name || '—'}</div>
          {dest?.line1 && <div style={{ fontSize: 14, marginTop: 4 }}>{dest.line1}</div>}
          {(dest?.city || dest?.state || dest?.zip) && (
            <div style={{ fontSize: 14 }}>
              {[dest?.city, dest?.state].filter(Boolean).join(', ')}{dest?.zip ? ` ${dest.zip}` : ''}
            </div>
          )}
          {dest?.phone && <div style={{ fontSize: 13, marginTop: 4, color: 'var(--color-text-muted)' }}>{formatPhone(dest.phone)}</div>}
        </section>
      )}

      {/* Clinical info — only available when OwlRx has the matching shipment */}
      {hasLocal && (
        <section style={clinicalBlock}>
          <div style={primaryLbl}>Rx details (from OwlRx)</div>
          <div style={twoCol}>
            {r.rx_number && <CompactKV label="Rx #" value={r.rx_number} />}
            {r.drug_name && <CompactKV label="Medication" value={r.drug_name} />}
            {r.copay != null && <CompactKV label="Copay" value={`$${r.copay.toFixed(2)}`} />}
          </div>
        </section>
      )}
      {!hasLocal && (
        <div style={localMissNote}>
          This order isn't tracked in OwlRx — medication and copay aren't available
          (drug name is never sent to the courier; copay lives in the pharmacy's claims data).
        </div>
      )}

      {/* Belongs-to + pickup */}
      <section style={sectMargin}>
        <div style={lbl}>Belongs to</div>
        <div style={{ fontSize: 15, fontWeight: 600 }}>
          {r.result_pharmacy_name || pickup?.name || 'Unknown pharmacy'}
        </div>
        {pickup && (pickup.line1 || pickup.city) && (
          <>
            {pickup.line1 && <div style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>{pickup.line1}</div>}
            {(pickup.city || pickup.state || pickup.zip) && (
              <div style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>
                {[pickup.city, pickup.state].filter(Boolean).join(', ')}{pickup.zip ? ` ${pickup.zip}` : ''}
              </div>
            )}
          </>
        )}
      </section>

      {r.reference && (
        <div style={{ fontSize: 12, color: 'var(--color-text-muted)', marginTop: 4 }}>
          Carrier reference: {r.reference}
        </div>
      )}

      {/* Timing */}
      <section style={sectMargin}>
        <div style={lbl}>Timing</div>
        <div style={twoCol}>
          {r.created_at && <TimingRow label="Created" iso={r.created_at} />}
          {r.dispatched_at && <TimingRow label="Dispatched" iso={r.dispatched_at} />}
          {r.picked_up_at && <TimingRow label="Picked up" iso={r.picked_up_at} />}
          {r.pickup_scheduled_at && <TimingRow label="Pickup scheduled" iso={r.pickup_scheduled_at} />}
          {r.delivery_scheduled_at && <TimingRow label="Delivery due" iso={r.delivery_scheduled_at} />}
          {r.delivered_at && <TimingRow label="Delivered" iso={r.delivered_at} />}
          {r.pod_signature_name && (
            <div>
              <div style={lbl}>Signed by</div>
              <div style={{ fontSize: 13 }}>{r.pod_signature_name}</div>
            </div>
          )}
        </div>
        <PodDownload orderNumber={r.order_number} />
      </section>

      {/* Carrier financials + contents */}
      {(r.pieces != null || r.weight != null || r.amount_charged != null || r.service_code) && (
        <section style={sectMargin}>
          <div style={lbl}>Carrier</div>
          <div style={twoCol}>
            {r.pieces != null && <CompactKV label="Pieces" value={String(r.pieces)} />}
            {r.weight != null && <CompactKV label="Weight" value={`${r.weight} lb`} />}
            {r.amount_charged != null && <CompactKV label="Carrier charge" value={`$${r.amount_charged.toFixed(2)}`} />}
            {r.service_code && <CompactKV label="Service" value={r.service_code} />}
          </div>
        </section>
      )}

      {r.notes && (
        <div style={{ marginTop: 12, padding: 8, background: 'var(--color-bg, #f8fafc)', border: '1px solid var(--color-border)', borderRadius: 6 }}>
          <div style={lbl}>Driver instructions</div>
          <div style={{ fontSize: 13 }}>{r.notes}</div>
        </div>
      )}

      {/* Events */}
      {r.events && r.events.length > 0 && (
        <section style={sectMargin}>
          <div style={lbl}>Activity</div>
          <ol style={{ listStyle: 'none', margin: '6px 0 0', padding: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
            {r.events.map((e, i) => (
              <li key={i} style={{ fontSize: 13 }}>
                <span style={{ fontWeight: 500 }}>{e.description || e.status}</span>
                <span style={{ marginLeft: 6, fontSize: 11, color: 'var(--color-text-muted)' }}>
                  {e.location ? `${e.location} · ` : ''}{new Date(e.occurred_at).toLocaleString()}
                </span>
              </li>
            ))}
          </ol>
        </section>
      )}

      <div style={{ marginTop: 14, paddingTop: 10, borderTop: '1px solid var(--color-border)', fontSize: 11, color: 'var(--color-text-muted)' }}>
        This lookup has been logged. Global order search results are not added to your worklist or reports.
      </div>
    </div>
  )
}

function TimingRow({ label, iso }: { label: string; iso: string }) {
  return (
    <div>
      <div style={lbl}>{label}</div>
      <div style={{ fontSize: 13 }}>{new Date(iso).toLocaleString()}</div>
    </div>
  )
}
function CompactKV({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div style={lbl}>{label}</div>
      <div style={{ fontSize: 13, fontWeight: 600 }}>{value}</div>
    </div>
  )
}
function StatusPill({ status }: { status: string }) {
  const tone = ({ created: '#6b7280', label_purchased: '#0369a1', in_transit: '#0369a1',
    out_for_delivery: '#a16207', delivered: '#15803d', exception: '#dc2626', cancelled: '#6b7280' } as Record<string, string>)[status] ?? '#6b7280'
  const label = ({ created: 'Created', label_purchased: 'Label purchased', in_transit: 'In transit',
    out_for_delivery: 'Out for delivery', delivered: 'Delivered', exception: 'Exception', cancelled: 'Cancelled' } as Record<string, string>)[status] ?? status
  return <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600, border: '1px solid', background: `${tone}1a`, color: tone, borderColor: `${tone}55` }}>{label}</span>
}

const page: React.CSSProperties = { padding: '20px clamp(16px, 4vw, 32px)', maxWidth: 'var(--page-max, 820px)', margin: '0 auto' }
const h1: React.CSSProperties = { fontSize: 22, fontWeight: 700 }
const muted: React.CSSProperties = { color: 'var(--color-text-muted)', fontSize: 13 }
const privNotice: React.CSSProperties = { display: 'flex', gap: 10, padding: 12, marginTop: 12, background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 8 }
const searchRow: React.CSSProperties = { display: 'flex', gap: 8, marginTop: 14, alignItems: 'stretch' }
const searchInput: React.CSSProperties = { width: '100%', padding: '8px 32px 8px 32px', fontSize: 13, border: '1px solid var(--color-border)', borderRadius: 8, boxSizing: 'border-box', background: 'var(--color-surface)' }
const clearBtn: React.CSSProperties = { position: 'absolute', right: 8, top: 7, padding: 2, background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)' }
const lookupBtn: React.CSSProperties = { padding: '0 18px', fontSize: 13, fontWeight: 600, background: 'var(--color-primary, #0369a1)', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer' }
const errBox: React.CSSProperties = { marginTop: 12, padding: '8px 12px', background: '#fee2e2', color: '#991b1b', border: '1px solid #fca5a5', borderRadius: 6, fontSize: 13 }
const card: React.CSSProperties = { background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 8, padding: 16 }
const twoCol: React.CSSProperties = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16, marginTop: 12 }
const lbl: React.CSSProperties = { fontSize: 10, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600, marginBottom: 2 }
const chip: React.CSSProperties = { fontSize: 11, padding: '1px 6px', border: '1px solid var(--color-border)', borderRadius: 4, color: 'var(--color-text-muted)' }
const typeTag: React.CSSProperties = { fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 4, background: '#a162071a', color: '#a16207', border: '1px solid #a1620755' }
const primaryBlock: React.CSSProperties = { padding: '14px 16px', background: 'var(--color-bg, #f8fafc)', border: '1px solid var(--color-border)', borderRadius: 8, marginBottom: 12 }
const primaryLbl: React.CSSProperties = { fontSize: 10, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700, marginBottom: 6 }
const clinicalBlock: React.CSSProperties = { padding: '12px 14px', background: '#ecfeff', border: '1px solid #67e8f9', borderRadius: 8, marginBottom: 12 }
const localMissNote: React.CSSProperties = { padding: '8px 12px', background: 'var(--color-bg, #f8fafc)', border: '1px dashed var(--color-border)', borderRadius: 6, fontSize: 11, color: 'var(--color-text-muted)', marginBottom: 12 }
const sectMargin: React.CSSProperties = { marginTop: 14 }
